import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { generateChipVideo } from '../services/geminiService.ts';
import { useStore } from '../context/StoreContext.tsx';
import { AssetType, AssetStatus } from '../types.ts';
import { FORMATS } from '../constants.ts';
import { Loader2, Video, Key, Upload, X, Download, Save, AlertCircle, Image as ImageIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../lib/firebase.ts';
import { useAuth } from '../context/AuthContext.tsx';

// Veo only supports 16:9 and 9:16
const VIDEO_FORMATS = FORMATS.filter(f => f.ratio === '16:9' || f.ratio === '9:16');

const VideoStudio: React.FC = () => {
  const { dna, addAsset, campaigns, assets } = useStore();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [prompt, setPrompt] = useState('Chip doing a happy dance loop');
  const [referenceImage, setReferenceImage] = useState<string | null>(null);
  const [selectedFormat, setSelectedFormat] = useState(VIDEO_FORMATS[0].id); // Default to Landscape
  const [selectedCampaign, setSelectedCampaign] = useState('');
  
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [videoUrl, setVideoUrl] = useState<string | null>(null); // This will hold the Blob URL
  const [error, setError] = useState<string | null>(null);

  // Asset Picker State
  const [isAssetPickerOpen, setIsAssetPickerOpen] = useState(false);
  const imageAssets = assets.filter(a => a.type === AssetType.Image);

  // Load default DNA likeness if available
  useEffect(() => {
    if (dna.visual.likenessImage && !referenceImage) {
      // Just visually, we don't default it unless user explicitly picks to avoid confusion
    }
  }, [dna]);

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setReferenceImage(reader.result as string);
        setError(null);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSelectAsset = (assetUrl: string) => {
    // Instant selection - defer loading/processing to generation time
    setReferenceImage(assetUrl);
    setIsAssetPickerOpen(false);
    setError(null);
  };

  const handleGenerate = async () => {
    setError(null);
    setIsGenerating(true);
    setVideoUrl(null);
    
    try {
      const format = VIDEO_FORMATS.find(f => f.id === selectedFormat) || VIDEO_FORMATS[0];
      const ratio = format.ratio;

      // 1. Generate the video (returns a temporary download link)
      // The service will handle downloading/processing the referenceImage if it's a URL
      const downloadLink = await generateChipVideo(prompt, referenceImage, ratio);
      
      // 2. Immediately fetch the video as a Blob to ensure it's playable and persistent in the session
      const response = await fetch(downloadLink);
      if (!response.ok) throw new Error("Failed to download generated video content.");
      const videoBlob = await response.blob();
      const localUrl = URL.createObjectURL(videoBlob);
      
      setVideoUrl(localUrl);

    } catch (err: any) {
      console.error("Video Generation Error:", err);
      if (err.message === "API_KEY_MISSING") {
        setError("Please select an API Key to use the Veo model.");
      } else {
        setError(err.message || "Failed to generate video.");
      }
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSaveToLibrary = async () => {
    if (!videoUrl || !user || !storage) return;
    
    setIsSaving(true);
    setError(null);

    try {
      // 1. Fetch the blob from our local object URL
      const response = await fetch(videoUrl);
      const blob = await response.blob();

      // 2. Upload to Firebase Storage
      const filename = `assets/${user.uid}/video_${Date.now()}.mp4`;
      const storageRef = ref(storage, filename);
      await uploadBytes(storageRef, blob);
      const permanentUrl = await getDownloadURL(storageRef);

      // 3. Save Asset Record
      const format = VIDEO_FORMATS.find(f => f.id === selectedFormat);
      
      await addAsset({
        id: `vid-${Date.now()}`,
        url: permanentUrl,
        type: AssetType.Video,
        createdAt: Date.now(),
        scenario: prompt,
        format: format?.name || '16:9',
        status: AssetStatus.Draft,
        campaignId: selectedCampaign || undefined,
        tags: ['video', 'veo', 'generated'],
        meta: { prompt }
      });
      navigate('/library');
    } catch (e) {
      console.error(e);
      setError("Failed to save video to storage.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleSelectKey = async () => {
    const win = window as any;
    if (win.aistudio?.openSelectKey) {
      await win.aistudio.openSelectKey();
      setError(null);
    }
  };

  return (
    <div className="max-w-6xl mx-auto h-[calc(100vh-140px)] flex flex-col gap-6 relative">
      <div>
        <h1 className="text-2xl font-heading font-bold text-[var(--text-primary)]">
          Video Studio
        </h1>
        <p className="text-[var(--text-secondary)] mt-1">Create animated loops with Veo 3.</p>
      </div>

      <div className="flex flex-col lg:flex-row gap-6 h-full min-h-0">
        {/* Configuration Panel */}
        <div className="w-full lg:w-1/3 bg-[var(--bg-primary)] rounded-xl border border-[var(--border-subtle)] p-6 overflow-y-auto flex flex-col gap-6">
          
          {/* Reference Image */}
          <section>
            <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">Reference Image (Optional)</label>
            {referenceImage ? (
               <div className="relative aspect-video rounded-lg overflow-hidden border border-[var(--border-default)] group bg-[var(--bg-tertiary)]">
                 <img src={referenceImage} alt="Ref" className="w-full h-full object-cover" />
                 <button 
                  onClick={() => setReferenceImage(null)}
                  className="absolute top-2 right-2 p-1.5 bg-black/50 text-white rounded-full hover:bg-red-500 transition-colors"
                 >
                   <X size={14} />
                 </button>
               </div>
            ) : (
              <div className="space-y-3">
                <label className="block w-full border-2 border-dashed border-[var(--border-default)] rounded-lg p-6 text-center cursor-pointer hover:border-[var(--accent-primary)] hover:bg-[var(--bg-secondary)] transition-colors">
                  <Upload className="mx-auto text-[var(--text-tertiary)] mb-2" size={24} />
                  <span className="text-sm font-medium text-[var(--text-primary)]">Upload Reference Image</span>
                  <input type="file" className="hidden" accept="image/*" onChange={handleImageUpload} />
                </label>
                
                <div className="flex items-center gap-2">
                   <div className="h-px bg-[var(--border-subtle)] flex-1"></div>
                   <span className="text-[10px] text-[var(--text-tertiary)] font-bold uppercase">OR</span>
                   <div className="h-px bg-[var(--border-subtle)] flex-1"></div>
                </div>

                <button 
                  onClick={() => setIsAssetPickerOpen(true)}
                  className="w-full py-2.5 border border-[var(--border-default)] rounded-lg text-sm font-medium text-[var(--text-secondary)] hover:bg-[var(--bg-secondary)] hover:text-[var(--text-primary)] flex items-center justify-center gap-2 transition-colors"
                >
                  <ImageIcon size={16} /> Select from Library
                </button>
              </div>
            )}
            <p className="text-xs text-[var(--text-secondary)] mt-2">
              Provide a scene or character reference to guide the generation.
            </p>
          </section>

          {/* Prompt */}
          <section>
            <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">Motion Prompt</label>
            <textarea
              className="input-field text-sm"
              rows={4}
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="Describe the movement..."
            />
            <div className="flex gap-2 mt-2 overflow-x-auto pb-1">
               {['Wave', 'Happy Dance', 'Thumbs Up', 'Walking'].map(t => (
                 <button 
                   key={t}
                   onClick={() => setPrompt(`Chip doing a ${t.toLowerCase()} loop`)}
                   className="px-2 py-1 bg-[var(--bg-tertiary)] text-[var(--text-primary)] border border-[var(--border-default)] text-xs rounded-full hover:bg-[var(--bg-secondary)] whitespace-nowrap"
                 >
                   {t}
                 </button>
               ))}
            </div>
          </section>

          {/* Settings */}
          <section className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">Aspect Ratio</label>
              <select 
                value={selectedFormat}
                onChange={(e) => setSelectedFormat(e.target.value)}
                className="input-field"
              >
                {VIDEO_FORMATS.map(f => (
                  <option key={f.id} value={f.id}>{f.name}</option>
                ))}
              </select>
              <p className="text-[10px] text-[var(--text-tertiary)] mt-1">Veo supports 16:9 & 9:16 only.</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">Duration</label>
              <select className="input-field opacity-60" disabled>
                <option>6 seconds (Default)</option>
              </select>
            </div>
          </section>

          {/* Campaign Link */}
          <section>
             <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">Link to Campaign</label>
             <select 
               value={selectedCampaign}
               onChange={(e) => setSelectedCampaign(e.target.value)}
               className="input-field"
             >
               <option value="">None</option>
               {campaigns.filter(c => c.status === 'active').map(c => (
                 <option key={c.id} value={c.id}>{c.name}</option>
               ))}
             </select>
          </section>

          {error && (
            <div className="p-3 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 rounded-lg text-xs flex flex-col gap-2 border border-red-200 dark:border-red-800 animate-fadeIn">
               <div className="flex items-center gap-2 font-medium">
                  <AlertCircle size={16} className="flex-shrink-0" />
                  <span>Error</span>
               </div>
              <span className="flex-1 opacity-90 leading-relaxed">{error}</span>
              {error.includes("API Key") && (
                <button 
                  onClick={handleSelectKey}
                  className="mt-1 w-full py-1.5 bg-[var(--bg-primary)] border border-red-200 dark:border-red-800 rounded text-xs font-semibold hover:bg-[var(--bg-secondary)]"
                >
                   Select Paid Key
                </button>
              )}
            </div>
          )}

          <div className="mt-auto">
             <button
              onClick={handleGenerate}
              disabled={isGenerating || isSaving}
              className="w-full py-3 bg-[var(--text-primary)] text-[var(--bg-primary)] rounded-lg font-bold hover:scale-[1.02] transition-transform disabled:opacity-50 flex items-center justify-center gap-2 shadow-lg"
            >
              {isGenerating ? <Loader2 className="animate-spin" size={20} /> : <Video size={20} />}
              Generate Video
            </button>
            <p className="text-[10px] text-center text-[var(--text-tertiary)] mt-2">
              Powered by Veo 3. Requires paid billing.
            </p>
          </div>
        </div>

        {/* Preview Panel - Minimal & Light */}
        <div className="flex-1 bg-[var(--bg-secondary)] border border-[var(--border-subtle)] rounded-xl overflow-hidden flex flex-col relative shadow-sm">
           <div className="flex-1 flex items-center justify-center relative p-4">
             {videoUrl ? (
               <div className="relative w-full h-full flex items-center justify-center">
                 <video 
                    controls 
                    autoPlay 
                    loop 
                    className="max-w-full max-h-full shadow-lg rounded-lg border border-[var(--border-subtle)]"
                  >
                   <source src={videoUrl} type="video/mp4" />
                   Your browser does not support video.
                 </video>
               </div>
             ) : (
               <div className="text-[var(--text-tertiary)] flex flex-col items-center w-full h-full justify-center">
                  {isGenerating ? (
                    <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-[var(--bg-secondary)] overflow-hidden">
                       <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[140%] h-[140%] bg-[radial-gradient(circle_at_center,var(--accent-primary)_0%,transparent_70%)] opacity-5 blur-[100px] animate-[pulse_4s_ease-in-out_infinite]" />
                       
                       <div className="relative mb-8">
                         <div className="w-24 h-24 rounded-full border border-[var(--border-subtle)] flex items-center justify-center relative">
                            <div className="absolute inset-0 border-t-2 border-[var(--accent-primary)] rounded-full animate-spin" />
                            <div className="absolute inset-1 border-b-2 border-purple-500 rounded-full animate-[spin_2s_linear_infinite_reverse]" />
                            <div className="w-12 h-12 bg-[var(--bg-tertiary)] rounded-full flex items-center justify-center shadow-inner relative z-10 animate-pulse">
                               <Video className="text-[var(--text-primary)]" size={20} />
                            </div>
                         </div>
                       </div>
                       
                       <div className="text-center relative z-10 space-y-2">
                         <h3 className="text-xl font-heading font-bold text-[var(--text-primary)] tracking-tight">
                           Rendering Video
                         </h3>
                         <div className="flex items-center justify-center gap-1.5">
                           <span className="w-1.5 h-1.5 bg-[var(--accent-primary)] rounded-full animate-[bounce_1s_infinite_0ms]" />
                           <span className="w-1.5 h-1.5 bg-[var(--accent-primary)] rounded-full animate-[bounce_1s_infinite_100ms]" />
                           <span className="w-1.5 h-1.5 bg-[var(--accent-primary)] rounded-full animate-[bounce_1s_infinite_200ms]" />
                         </div>
                         <p className="text-xs text-[var(--text-secondary)] font-mono opacity-80 mt-4">
                           Processing reference image & generating video frames...
                         </p>
                       </div>
                    </div>
                  ) : (
                    <>
                      <div className="w-20 h-20 rounded-full bg-[var(--bg-primary)] border border-[var(--border-subtle)] flex items-center justify-center mb-4 shadow-sm">
                        <Video size={32} className="opacity-30" />
                      </div>
                      <p className="text-[var(--text-secondary)] font-medium">Video preview will appear here</p>
                    </>
                  )}
               </div>
             )}
           </div>
           
           {/* Actions Bar - Minimal */}
           <div className="h-16 bg-[var(--bg-primary)] border-t border-[var(--border-subtle)] flex items-center justify-between px-6">
              <div className="text-xs text-[var(--text-secondary)] font-medium">
                {videoUrl ? `Generated with Veo 3 • ${VIDEO_FORMATS.find(f => f.id === selectedFormat)?.name || 'Custom'}` : ''}
              </div>
              <div className="flex gap-3">
                 <button 
                   disabled={!videoUrl || isSaving}
                   onClick={() => setVideoUrl(null)} 
                   className="px-4 py-2 text-sm text-[var(--text-secondary)] hover:text-[var(--text-primary)] disabled:opacity-30 transition-colors"
                 >
                   Clear
                 </button>
                 <a 
                   href={videoUrl || '#'} 
                   download="chip-video.mp4"
                   className={`px-4 py-2 bg-[var(--bg-secondary)] text-[var(--text-primary)] border border-[var(--border-subtle)] rounded-lg text-sm font-medium hover:bg-[var(--bg-tertiary)] flex items-center gap-2 ${(!videoUrl || isSaving) && 'pointer-events-none opacity-50'}`}
                 >
                   <Download size={16} /> Download
                 </a>
                 <button 
                   onClick={handleSaveToLibrary}
                   disabled={!videoUrl || isSaving}
                   className="px-6 py-2 bg-[var(--text-primary)] text-[var(--bg-primary)] rounded-lg text-sm font-medium hover:scale-105 transition-transform disabled:opacity-50 flex items-center gap-2 shadow-md"
                 >
                   {isSaving ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />}
                   {isSaving ? 'Saving...' : 'Save to Library'}
                 </button>
              </div>
           </div>
        </div>
      </div>

      {/* Asset Picker Modal */}
      {isAssetPickerOpen && createPortal(
        <div className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fadeIn">
          <div className="bg-[var(--bg-primary)] rounded-xl shadow-2xl w-full max-w-4xl h-[80vh] flex flex-col border border-[var(--border-subtle)] animate-slideUp">
             <div className="flex items-center justify-between p-4 border-b border-[var(--border-subtle)]">
                <h3 className="font-bold text-lg text-[var(--text-primary)]">Select Reference Image</h3>
                <button onClick={() => setIsAssetPickerOpen(false)} className="p-1 hover:bg-[var(--bg-secondary)] rounded-full">
                  <X size={20} />
                </button>
             </div>
             
             <div className="flex-1 overflow-y-auto p-4 bg-[var(--bg-tertiary)]/30">
               {imageAssets.length > 0 ? (
                 <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                   {imageAssets.map(asset => (
                     <button 
                       key={asset.id}
                       onClick={() => handleSelectAsset(asset.url)}
                       className="relative aspect-square rounded-lg overflow-hidden border border-[var(--border-subtle)] hover:border-[var(--accent-primary)] hover:ring-2 ring-[var(--accent-primary)]/20 transition-all group bg-[var(--bg-primary)]"
                     >
                       <img src={asset.url} alt={asset.scenario} className="w-full h-full object-cover" />
                       <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
                       <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                         <p className="text-[10px] text-white truncate">{asset.scenario}</p>
                       </div>
                     </button>
                   ))}
                 </div>
               ) : (
                 <div className="h-full flex flex-col items-center justify-center text-[var(--text-tertiary)]">
                   <ImageIcon size={48} className="mb-4 opacity-20" />
                   <p>No images found in Asset Library.</p>
                 </div>
               )}
             </div>
          </div>
        </div>,
        document.body
      )}

    </div>
  );
};

export default VideoStudio;